"use client";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Edit, Smartphone, Monitor, FileCode, Code2, ExternalLink } from "lucide-react";

export default function Preview() {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState("desktop");
  const [currentPage, setCurrentPage] = useState("index.html");
  const [pages, setPages] = useState({});
  const [showFiles, setShowFiles] = useState(true);

  useEffect(() => {
    const savedPages = localStorage.getItem("generatedPages");
    const savedCode = localStorage.getItem("generatedCode");
    
    if (savedPages && savedPages !== "null") {
      try {
        const parsed = JSON.parse(savedPages);
        if (parsed && typeof parsed === 'object') {
          setPages(parsed);
          const keys = Object.keys(parsed);
          if (keys.length > 0) setCurrentPage(keys[0]);
        } else if (savedCode) {
          setPages({ "index.html": savedCode });
          setCurrentPage("index.html");
        }
      } catch {
        if (savedCode) {
          setPages({ "index.html": savedCode });
          setCurrentPage("index.html");
        }
      }
    } else if (savedCode) {
      setPages({ "index.html": savedCode });
      setCurrentPage("index.html");
    }
  }, []);

  const getCurrentCode = () => {
    return pages[currentPage] || "";
  };

  const downloadAllFiles = () => {
    const zipContent = Object.entries(pages).map(([filename, content]) => {
      return `--- ${filename} ---\n${content}`;
    }).join('\n\n');
    
    const blob = new Blob([zipContent], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "website-files.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadSingleFile = (filename, content) => {
    let cleaned = content.trim();
    cleaned = cleaned.replace(/^```[\w]*\n?/, '').replace(/\n?```$/, '');
    const html = cleaned.startsWith("<!DOCTYPE") || cleaned.startsWith("<html")
      ? cleaned
      : getSrcDoc(cleaned);
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getSrcDoc = (code) => {
    if (!code) return "";
    let cleaned = code.trim();
    cleaned = cleaned.replace(/^```[\w]*\n?/, '').replace(/\n?```$/, '');
    if (cleaned.startsWith("<!DOCTYPE") || cleaned.startsWith("<html")) {
      return cleaned;
    }
    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Lova Website</title>
  <script src="https://cdn.tailwindcss.com"></script>
  <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&family=Playfair+Display:wght@400;500;600;700&family=Space+Grotesk:wght@400;500;600;700&family=Manrope:wght@300;400;500;600;700&display=swap" rel="stylesheet">
  <script>
    tailwind.config = {
      theme: {
        extend: {
          fontFamily: {
            display: ['Playfair Display', 'serif'],
            sans: ['DM Sans', 'sans-serif'],
            mono: ['Space Grotesk', 'monospace'],
            body: ['Manrope', 'sans-serif'],
          }
        }
      }
    }
  </script>
  <style>
    * { scroll-behavior: smooth; box-sizing: border-box; }
    body { font-family: 'DM Sans', sans-serif; margin: 0; padding: 0; }
    img { max-width: 100%; height: auto; display: block; }
    @keyframes fadeInUp { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-20px); } }
    @keyframes pulse-glow { 0%, 100% { box-shadow: 0 0 20px rgba(99, 102, 241, 0.3); } 50% { box-shadow: 0 0 40px rgba(99, 102, 241, 0.6); } }
    @keyframes slideIn { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
    @keyframes scaleIn { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
    .animate-fadeInUp { animation: fadeInUp 0.8s ease-out forwards; }
    .animate-float { animation: float 6s ease-in-out infinite; }
    .animate-pulse-glow { animation: pulse-glow 2s ease-in-out infinite; }
    .animate-slideIn { animation: slideIn 0.6s ease-out forwards; }
    .animate-scaleIn { animation: scaleIn 0.5s ease-out forwards; }
  </style>
</head>
<body style="background: white;">
  ${cleaned}
  <script>
    document.addEventListener('DOMContentLoaded', function() {
      document.querySelectorAll('img').forEach(function(img) {
        if (img.complete) {
          img.style.opacity = '1';
        } else {
          img.onload = function() { img.style.opacity = '1'; };
          img.onerror = function() { img.style.opacity = '0.5'; img.alt = 'Image not found'; };
        }
      });
    });
  </script>
</body>
</html>`;
  };

  const pageKeys = Object.keys(pages);

  return (
    <div className="h-screen flex flex-col bg-[#050505] text-white overflow-hidden">
      {/* Top Navbar */}
      <div className="bg-white/[0.02] backdrop-blur-xl border-b border-white/10 px-4 py-2 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-zinc-400 hover:text-white transition group">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm hidden sm:inline">Back</span>
          </button>
          <div className="h-5 w-px bg-white/10" />
          <span className="text-zinc-400 text-xs">Preview</span>
          {pageKeys.length > 1 && (
            <>
              <div className="h-5 w-px bg-white/10" />
              <div className="flex items-center gap-1">
                {pageKeys.map((name) => (
                  <button
                    key={name}
                    onClick={() => setCurrentPage(name)}
                    className={`text-xs px-2 py-1 rounded transition ${
                      currentPage === name
                        ? "bg-violet-600 text-white"
                        : "text-zinc-500 hover:text-zinc-300"
                    }`}
                  >
                    {name.replace('.html', '')}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="flex bg-white/5 rounded-lg p-0.5 border border-white/10">
            <button
              onClick={() => setViewMode("desktop")}
              className={`p-1.5 rounded-md transition ${viewMode === "desktop" ? "bg-violet-600 text-white" : "text-zinc-400 hover:text-white"}`}
            >
              <Monitor className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode("mobile")}
              className={`p-1.5 rounded-md transition ${viewMode === "mobile" ? "bg-violet-600 text-white" : "text-zinc-400 hover:text-white"}`}
            >
              <Smartphone className="w-4 h-4" />
            </button>
          </div>

          <button
            onClick={() => setShowFiles(!showFiles)}
            className={`p-1.5 rounded-lg text-xs transition ${showFiles ? "bg-violet-600" : "bg-white/5 hover:bg-white/10 border border-white/10"}`}
            title="Toggle file sidebar"
          >
            <FileCode className="w-4 h-4" />
          </button>

          <button onClick={() => navigate("/modify")} className="flex items-center gap-1.5 px-3 py-1.5 bg-fuchsia-600/20 hover:bg-fuchsia-600 border border-fuchsia-500/30 hover:border-fuchsia-400 rounded-lg text-xs transition">
            <Edit className="w-3.5 h-3.5" /> Edit
          </button>
          <button onClick={downloadAllFiles} className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 hover:bg-violet-500 rounded-lg text-xs transition">
            <Download className="w-3.5 h-3.5" /> Download
          </button>
          <button onClick={() => navigate("/deploy")} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-500 rounded-lg text-xs transition">
            <ExternalLink className="w-3.5 h-3.5" /> Export
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left sidebar - files */}
        {showFiles && pageKeys.length > 0 && (
          <div className="w-48 bg-white/[0.02] border-r border-white/10 p-3 flex flex-col shrink-0 overflow-y-auto">
            <h3 className="text-xs font-semibold text-zinc-500 mb-2 flex items-center gap-1.5 uppercase tracking-wider">
              <Code2 className="w-3 h-3" />
              Files
            </h3>
            <div className="space-y-0.5 flex-1">
              {pageKeys.map((filename) => (
                <button
                  key={filename}
                  onClick={() => setCurrentPage(filename)}
                  className={`w-full text-left px-2.5 py-1.5 rounded text-xs transition flex items-center gap-2 ${
                    currentPage === filename 
                      ? "bg-violet-600/20 text-violet-300 border border-violet-500/30" 
                      : "text-zinc-400 hover:text-white hover:bg-white/5"
                  }`}
                >
                  <FileCode className="w-3 h-3 shrink-0" />
                  <span className="truncate">{filename}</span>
                </button>
              ))}
            </div>
            <button
              onClick={() => downloadSingleFile(currentPage, pages[currentPage])}
              className="mt-3 w-full px-2.5 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded text-xs text-zinc-400 hover:text-white transition flex items-center justify-center gap-1.5"
            >
              <Download className="w-3 h-3" />
              Download
            </button>
          </div>
        )}

        {/* Preview area - ~75% of remaining space */}
        <div className="flex-1 flex items-start justify-center bg-gradient-to-b from-[#0a0a0f] to-[#050505] overflow-y-auto p-4 md:p-6">
          <div className={`w-full transition-all duration-300 self-stretch flex flex-col ${viewMode === "mobile" ? "max-w-[375px]" : "max-w-[80%]"}`}>
            {viewMode === "mobile" && (
              <div className="flex justify-center mb-2 shrink-0">
                <div className="w-[375px] h-8 bg-black/80 rounded-t-2xl flex items-center justify-center">
                  <div className="w-16 h-4 bg-black rounded-full" />
                </div>
              </div>
            )}
            <div className={`bg-white rounded-xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50 flex-1 flex transition-all ${viewMode === "mobile" ? "shadow-violet-500/10" : ""}`}>
              {pageKeys.length > 0 ? (
                <iframe
                  srcDoc={getSrcDoc(getCurrentCode())}
                  className="w-full flex-1 border-0 min-h-[400px]"
                  title="Website Preview"
                />
              ) : (
                <div className="w-full h-[500px] bg-gradient-to-br from-slate-900 to-slate-800 flex flex-col items-center justify-center p-8 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center mb-4">
                    <FileCode className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">No Website Generated</h3>
                  <p className="text-zinc-400 mb-4 text-sm max-w-md">Generate a website first to see the preview.</p>
                  <button onClick={() => navigate("/dashboard")} className="px-5 py-2.5 bg-violet-600 hover:bg-violet-500 rounded-xl text-sm font-medium transition">
                    Go to Dashboard
                  </button>
                </div>
              )}
            </div>
            {viewMode === "mobile" && (
              <div className="flex justify-center mt-2">
                <div className="w-[375px] h-8 bg-black/80 rounded-b-2xl" />
              </div>
            )}
          </div>
        </div>


      </div>
    </div>
  );
}
